import type { CSSProperties } from 'react';
import type { Column } from '@tanstack/react-table';
import type { TableLayout, TableRowData } from '../types';

/** TanStack's default for an unset maxSize — treat as "no cap". */
const UNSET_MAX_SIZE = Number.MAX_SAFE_INTEGER;

export interface ColumnSize {
  size: number;
  minSize: number;
  maxSize: number;
  /** Fixed layout only: take the table's remaining width (see `fill`). */
  fill?: boolean;
}

/** Reads the `fill` flag that useDataTableColumns resolves into column meta. */
export function isFillColumn(meta: unknown): boolean {
  return (
    typeof meta === 'object' &&
    meta !== null &&
    'fill' in meta &&
    meta.fill === true
  );
}

export function readColumnSize<TRow extends TableRowData>(
  column: Column<TRow, unknown>
): ColumnSize {
  return {
    size: column.getSize(),
    minSize: column.columnDef.minSize ?? 0,
    maxSize: column.columnDef.maxSize ?? UNSET_MAX_SIZE,
    fill: isFillColumn(column.columnDef.meta),
  };
}

/**
 * Fixed layout: when a column fills, the table needs an explicit min-width so
 * the fill column never shrinks below its own `minWidth` (a `table-fixed` auto
 * column otherwise collapses on narrow viewports). Undefined when no column
 * fills, leaving other tables untouched.
 */
export function getFixedLayoutMinWidth(
  sizes: ColumnSize[],
  extra = 0
): number | undefined {
  if (!sizes.some(s => s.fill)) {
    return undefined;
  }
  return sizes.reduce((sum, s) => sum + (s.fill ? s.minSize : s.size), extra);
}

/**
 * Per-cell sizing style by layout.
 *
 * `fixed` pins an authoritative `width`. In `auto`, `max-width` is unreliable on
 * table cells, so a column's `maxWidth` is used only as the fixed/grow flag while
 * `min-width` (honored on cells) is always emitted as the floor:
 * - capped column (has `maxWidth`) → fixed `width` that holds.
 * - uncapped column → greedy `width: 100%` that absorbs surplus.
 *
 * The always-on `min-width` floor stops grow columns from squeezing fixed ones
 * below their minimum.
 */
export function buildColumnSizeStyle(
  layout: TableLayout,
  { size, minSize, maxSize, fill }: ColumnSize
): CSSProperties {
  if (layout !== 'auto') {
    // A fill column gets no width so `table-fixed` hands it the remaining space.
    return fill ? {} : { width: size };
  }

  const style: CSSProperties = {
    width: maxSize < UNSET_MAX_SIZE ? size : '100%',
  };
  if (minSize > 0) {
    style.minWidth = minSize;
  }
  return style;
}

/**
 * Narrowest width (px) at which a header cell still shows its content without
 * breaking a word or hiding a control: the content's min-content width plus
 * the cell's own padding. One word stays whole; several wrap, and the widest
 * sets the width. Measured on a hidden clone, so nothing reflows.
 */
export function measureHeaderMinWidth(cell: HTMLElement): number {
  const content = cell.firstElementChild;
  if (!(content instanceof HTMLElement)) return 0;
  const probe = content.cloneNode(true) as HTMLElement;
  probe.style.cssText =
    'position:absolute;visibility:hidden;pointer-events:none;width:min-content;white-space:normal';
  cell.appendChild(probe);
  // Fractional: `offsetWidth` rounds 77.48 down to 77, and the label then
  // breaks its one word.
  const { width } = probe.getBoundingClientRect();
  probe.remove();
  const { paddingLeft, paddingRight } = getComputedStyle(cell);
  return Math.ceil(
    width + (parseFloat(paddingLeft) || 0) + (parseFloat(paddingRight) || 0)
  );
}
